import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, MessageCircle, Plus, RotateCcw, Trash2 } from "lucide-react";
import {
  DTF_ART_SPACING,
  DTF_MAX_ARTS,
  DTF_PRICE_PER_METER,
  DTF_PRINT_WIDTH,
} from "@/config/dtf";
import {
  buildQuoteMessage,
  calculateDtf,
  formatBrl,
  formatNum,
  whatsappLink,
} from "@/lib/dtf";

const emptyArt = () => ({ width: "", height: "", quantity: "" });
const presets = [
  { label: "A5", width: "14,8", height: "21" },
  { label: "A4", width: "21", height: "29,7" },
  { label: "Camiseta", width: "28", height: "35" },
  { label: "Logo", width: "10", height: "10" },
];

const Field = ({ label, hint, testId, value, onChange, suffix, placeholder }) => (
  <label className="block">
    <span className="block text-[13px] font-semibold text-black">{label}</span>
    <span className="relative mt-2 block">
      <input
        type="text"
        inputMode="decimal"
        data-testid={testId}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, ""))}
        className="num h-14 w-full rounded-xl border border-neutral-200 bg-white px-4 pr-14 text-lg font-medium text-black outline-none transition-colors duration-200 placeholder:text-neutral-300 focus:border-[#f58244] focus:ring-4 focus:ring-[#f58244]/15"
      />
      {suffix && (
        <span className="num pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-neutral-400">
          {suffix}
        </span>
      )}
    </span>
    {hint && <span className="mt-1.5 block text-xs text-neutral-400">{hint}</span>}
  </label>
);

const ResultRow = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-white/10 py-3">
    <span className="text-sm text-white/60">{label}</span>
    <span data-testid={testId} className="num text-base font-semibold text-white">
      {value}
    </span>
  </div>
);

export const Calculator = () => {
  const [arts, setArts] = useState([emptyArt()]);
  const [price, setPrice] = useState(
    DTF_PRICE_PER_METER === null ? "" : String(DTF_PRICE_PER_METER).replace(".", ","),
  );

  const result = useMemo(
    () => calculateDtf({ arts, pricePerMeter: price }),
    [arts, price],
  );

  const update = (i, key, value) =>
    setArts((prev) => prev.map((a, idx) => (idx === i ? { ...a, [key]: value } : a)));

  const applyPreset = (i, p) =>
    setArts((prev) =>
      prev.map((a, idx) =>
        idx === i ? { ...a, width: p.width, height: p.height } : a,
      ),
    );

  const reset = () => {
    setArts([emptyArt()]);
  };

  return (
    <section id="calculadora" className="scroll-mt-20 bg-[#f6f6f5] py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <p className="num text-[11px] uppercase tracking-[0.28em] text-[#f58244]">
            Ferramenta
          </p>
          <h2 className="mt-3 text-3xl font-extrabold leading-tight text-black sm:text-4xl lg:text-[3.2rem]">
            Calculadora de Impressão DTF Têxtil
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600 sm:text-base">
            Descubra quanto de filme DTF têxtil sua arte utilizará e estime o
            valor da impressão em poucos segundos.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.25fr_0.75fr] lg:gap-8">
          {/* INPUTS */}
          <article className="rounded-[26px] border border-neutral-200 bg-white p-6 sm:p-8">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 pb-5">
              <div>
                <h3 className="text-lg font-bold text-black">Configuração</h3>
                <p className="mt-1 text-xs text-neutral-500">
                  Largura útil do filme DTF têxtil:{" "}
                  <strong className="num text-black">{formatNum(DTF_PRINT_WIDTH, 0)} cm</strong>
                  {" · "}cobrança por metro linear
                  {DTF_ART_SPACING > 0
                    ? ` · espaçamento ${formatNum(DTF_ART_SPACING, 1)} cm`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={reset}
                data-testid="calculator-reset"
                className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-4 py-2 text-xs font-semibold text-neutral-600 transition-colors hover:border-black hover:text-black"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Limpar
              </button>
            </header>

            <div className="mt-6 max-w-xs">
              <Field
                label="Valor por metro linear"
                hint={`1 metro linear = ${formatNum(DTF_PRINT_WIDTH, 0)} cm de largura x 100 cm de comprimento.`}
                testId="calculator-price-input"
                value={price}
                onChange={setPrice}
                suffix="R$/m"
                placeholder="0,00"
              />
            </div>

            <div className="mt-8 space-y-6">
              {arts.map((art, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-2xl border border-neutral-200 bg-[#fafaf9] p-5"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-[0.12em] text-black">
                      <span className="num mr-2 text-[#f58244]">0{i + 1}</span>
                      Estampa {i > 0 && <span className="text-neutral-400">(opcional)</span>}
                    </h4>
                    {arts.length > 1 && (
                      <button
                        type="button"
                        aria-label={`Remover estampa ${i + 1}`}
                        data-testid={`calculator-remove-art-${i + 1}`}
                        onClick={() => setArts((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-neutral-400 transition-colors hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {presets.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        data-testid={`calculator-preset-${p.label.toLowerCase()}-${i + 1}`}
                        onClick={() => applyPreset(i, p)}
                        className="num rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-600 transition-colors hover:border-[#f58244] hover:text-[#f58244]"
                      >
                        {p.label} · {p.width}x{p.height}
                      </button>
                    ))}
                  </div>

                  <div className="mt-5 grid gap-4 sm:grid-cols-3">
                    <Field
                      label="Largura"
                      testId={`calculator-width-input-${i + 1}`}
                      value={art.width}
                      onChange={(v) => update(i, "width", v)}
                      suffix="cm"
                      placeholder="0"
                    />
                    <Field
                      label="Altura"
                      testId={`calculator-height-input-${i + 1}`}
                      value={art.height}
                      onChange={(v) => update(i, "height", v)}
                      suffix="cm"
                      placeholder="0"
                    />
                    <Field
                      label="Quantidade"
                      testId={`calculator-quantity-input-${i + 1}`}
                      value={art.quantity}
                      onChange={(v) => update(i, "quantity", v)}
                      suffix="un"
                      placeholder="0"
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {arts.length < DTF_MAX_ARTS && (
              <button
                type="button"
                data-testid="calculator-add-art"
                onClick={() => setArts((prev) => [...prev, emptyArt()])}
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-dashed border-neutral-300 px-5 py-3 text-sm font-semibold text-neutral-700 transition-colors hover:border-[#f58244] hover:text-[#f58244]"
              >
                <Plus className="h-4 w-4" />
                Adicionar outra estampa
              </button>
            )}

            <AnimatePresence>
              {result.errors.length > 0 && (
                <motion.ul
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  data-testid="calculator-errors"
                  className="mt-6 space-y-2 overflow-hidden"
                >
                  {result.errors.map((e) => (
                    <li
                      key={e.index + e.message}
                      className="flex items-start gap-2 rounded-xl bg-[#fff4ed] px-4 py-3 text-xs font-medium text-[#b9491a]"
                    >
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      {e.message}
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>

            <p className="mt-6 text-xs leading-relaxed text-neutral-400">
              O cálculo é atualizado automaticamente conforme você preenche os
              campos. As estampas são encaixadas lado a lado dentro da largura
              útil de {formatNum(DTF_PRINT_WIDTH, 0)} cm e a cobrança considera
              apenas o comprimento de filme consumido.
            </p>
          </article>

          {/* RESULT */}
          <article className="lg:sticky lg:top-24 lg:self-start">
            <div className="grain relative overflow-hidden rounded-[26px] bg-black p-6 sm:p-8">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-[#f58244]/30 blur-3xl"
              />
              <div className="relative">
                <p className="num text-[11px] uppercase tracking-[0.28em] text-[#f58244]">
                  Seu cálculo
                </p>

                {!result.valid ? (
                  <p
                    data-testid="calculator-empty-state"
                    className="mt-6 text-sm leading-relaxed text-white/60"
                  >
                    Preencha largura, altura e quantidade da sua estampa para ver
                    quantos metros de DTF têxtil serão necessários e o valor
                    estimado.
                  </p>
                ) : (
                  <div className="mt-5">
                    <ResultRow
                      label="Quantidade total"
                      value={`${formatNum(result.quantity, 0)} un`}
                      testId="calculator-quantity-result"
                    />
                    <ResultRow
                      label="Faixas utilizadas no filme"
                      value={`${formatNum(result.rows, 0)}`}
                      testId="calculator-rows-result"
                    />
                    <ResultRow
                      label="Comprimento necessário"
                      value={`${formatNum(result.lengthCm, 1)} cm`}
                      testId="calculator-length-result"
                    />
                    <ResultRow
                      label="Metro linear necessário"
                      value={`${formatNum(result.meters)} m`}
                      testId="calculator-meters-result"
                    />
                    <ResultRow
                      label="Aproveitamento do filme"
                      value={`${formatNum(result.usage * 100, 0)}%`}
                      testId="calculator-usage-result"
                    />

                    <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/10">
                      <motion.div
                        className="h-full rounded-full bg-[#f58244]"
                        animate={{ width: `${Math.min(result.usage * 100, 100)}%` }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>

                    <div className="mt-7 rounded-2xl bg-[#f58244] p-5">
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">
                        Valor estimado
                      </p>
                      <p
                        data-testid="calculator-total-price"
                        className="num mt-1 text-[clamp(2.1rem,7vw,3.1rem)] font-black leading-none text-white"
                      >
                        {result.total === null ? "—" : formatBrl(result.total)}
                      </p>
                      <p className="mt-2 text-xs font-medium text-white/85">
                        {result.total === null
                          ? "Informe o valor por metro linear para ver o total."
                          : `${formatBrl(result.pricePerMeter)}/m linear · ${formatBrl(result.unitPrice)} por unidade`}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-7 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <p className="text-sm font-bold text-white">
                    Gostou do valor? Encomende seu DTF têxtil
                  </p>
                  <a
                    href={whatsappLink(buildQuoteMessage(result))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="calculator-whatsapp-submit"
                    className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#25d366] px-6 py-3.5 text-sm font-bold text-black transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#20bd5a]"
                  >
                    <MessageCircle className="h-4 w-4" strokeWidth={2.6} />
                    Envie sua arte agora
                  </a>
                  <p className="mt-3 text-[11px] leading-relaxed text-white/45">
                    Enviamos automaticamente os dados do seu cálculo na mensagem.
                    O resultado é uma estimativa e pode variar conforme a
                    montagem das artes.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};
